/**
 * Плашка «будет ли карточка и от какой ссылки» — docs/phase-1/05-ui.md §3.4, ПРАВИЛО 5.4
 * `docs/telegram.md`.
 *
 * Правдоподобный макет карточки НЕ рисуем. Это не экономия: карточку формирует Telegram по
 * чужой странице, её содержимого мы не знаем, и нарисованная заглушка создала бы у
 * утверждающего впечатление, что знаем. Честная плашка словами точнее и предупреждает
 * редакторски.
 *
 * ОДНА СТРОКА ВМЕСТО ЭКРАНА ТЕКСТА (правка 3.4 от 08.09.2026). До этой правки плашка была
 * самым длинным блоком экрана черновика: абзац про «попытается… отдаёт OG-данные… мы не
 * контролируем и заранее не знаем», служебная строка `источник → адрес`, правило про голый
 * домен и ДВЕ ВЫКЛЮЧЕННЫЕ кнопки с абзацем о том, что в фазе 1 они не работают. Что стало:
 *
 *   • ветка карточки — одна строка «Снизу прицепится карточка — {host}». Ровно это и требует
 *     ПРАВИЛО 5.4: видно, БУДЕТ ли карточка и КАКАЯ ссылка её порождает. Всё остальное —
 *     полный адрес, происхождение ссылки, оговорка про OG и правило голого домена — собрано
 *     в один `title` на той же строке;
 *   • ветки «карточки не будет» — по одной фразе каждая, длинное объяснение тоже в `title`;
 *   • выключенные кнопки «Отключить карточку» / «Другая ссылка…» удалены совсем. Маршрута
 *     правки карточки в фазе 1 нет ВООБЩЕ (09 §6.1), а выключенная кнопка обещает, что он
 *     появится по соседству. Карточка следует за текстом: она встаёт на первую ссылку, и
 *     управляют ей, правя текст, а не кнопкой рядом с ним.
 *
 * ФОРМУЛИРОВКА «ПОПЫТАЕТСЯ», А НЕ «НАРИСУЕТ» — по итогам калибровки 25.08.2026 — осталась,
 * но переехала в `title`. Оба поста первой живой публикации содержали ссылку, сервер в ответе
 * честно вернул выбранный им `url` карточки — а в клиенте карточки не появилось НИ У ОДНОГО:
 * Telegram не смог достать страницу. Выбор ссылки сервером и появление карточки у подписчика —
 * разные события, и второе от нас не зависит вовсе (ПРАВИЛО 6.2). Поэтому видимая строка
 * говорит про МЕСТО карточки, а развилку «страница может не отдать OG» несёт подсказка.
 *
 * ТРАНСПОРТА ТРИ, И ПЛАШКА ОБЯЗАНА НАЗВАТЬ СВОЙ. У обычного сообщения карточка снизу. У
 * расширенного её нет вовсе — параметра `link_preview_options` у этого метода не существует, —
 * но причин уехать расширенным несколько, и человеку они говорят разное: «картинка заняла это
 * место» видно глазом, а «текст перерос порог 4064» не видно ничем. Молчание при нескольких
 * причинах стало бы тихой потерей карточки, против которой плашка и написана. Причину выдаёт
 * `getFirstUrl`, здесь она не выводится заново.
 *
 * СТОРОНА КАРТОЧКИ НЕ ЗАШИТА СЛОВОМ «СНИЗУ». При `show_above_text` карточка встаёт НАД
 * текстом, и печатать в этом случае «снизу» значило бы соврать в панели согласования — то
 * есть сломать ровно то, ради чего плашка существует.
 *
 * ПРИЧИН УЕХАТЬ RICH С 19.09.2026 ЧЕТЫРЕ (docs/release-2026-09-19.md §9.3): картинка, файл,
 * длина и политика бренда «все посты — расширенным сообщением». Способ отправки при этом
 * ЧИТАЕТСЯ из сохранённой версии (`method`), а у несохранённой правки ВЫВОДИТСЯ по политике
 * СЕЙЧАС (`unsaved`, шапка `first-url.ts`): её `method` — от прошлой версии, и плашка по нему
 * показала бы карточку по вчерашнему решению.
 */

import { codePointLen, type DraftContent } from '@smm/contracts';
import { getFirstUrl } from '@smm/contracts/preview';
import { TRANSPORT_RICH_ALWAYS_DEFAULT } from '@smm/contracts/schemas';
import type { JSX, ReactNode } from 'react';
import { IconInfo, IconLink, IconWarn } from '../components/icons.tsx';
import styles from './PreviewPanel.module.css';
import { SEND_MESSAGE_THRESHOLD, withDerivedTransport } from './validation.ts';

const SOURCE_PHRASE: Readonly<Record<'explicit' | 'url_entity' | 'text_link', string>> = {
  explicit: 'ссылка задана в настройках карточки явно',
  url_entity: 'её порождает голая ссылка в тексте',
  text_link: 'её порождает ссылка, спрятанная в анкоре — анкор карточку не отменяет',
};

/**
 * Два контринтуитивных правила §5.2а. Раньше печатались отдельной строкой всегда; теперь
 * ездят в подсказке — и в ветке карточки, и в ветках без неё, потому что именно они
 * объясняют, ПОЧЕМУ карточка есть или её нет.
 */
const RULES =
  'Голый домен без пути карточку не даёт (example.ru — нет), а с путём — даёт (example.ru/blog — да). Ссылка под анкором карточку даёт всегда.';

const OG_NOTE =
  'Карточка появится, только если страница доступна и отдаёт OG-данные: её заголовок, ' +
  'описание и картинку мы не контролируем и заранее не знаем.';

const RICH_NOTE =
  'У расширенного сообщения параметра карточки нет вовсе. Ссылки в тексте останутся ' +
  'кликабельными, но страницу под них Telegram не развернёт.';

type CardOutcome = Extract<ReturnType<typeof getFirstUrl>, { kind: 'card' }>;
type Lpo = DraftContent['link_preview_options'];

/** Всё, что ушло из видимой части ветки карточки, — одной подсказкой и в одном порядке. */
function cardTitle(outcome: CardOutcome, lpo: Lpo): string {
  const facts = [
    outcome.url,
    lpo?.show_above_text === true ? 'встанет над текстом' : 'встанет под текстом',
  ];
  if (lpo?.prefer_large_media === true) facts.push('крупная картинка');
  if (lpo?.prefer_small_media === true) facts.push('мелкая картинка');

  const explicitUrl = lpo !== null && lpo.url !== null && lpo.url !== '';
  const sizeWithoutUrl =
    !explicitUrl && lpo !== null && (lpo.prefer_large_media || lpo.prefer_small_media);

  return [
    `${facts.join(' · ')}.`,
    `${SOURCE_PHRASE[outcome.source]}.`,
    OG_NOTE,
    sizeWithoutUrl
      ? 'Размер картинки задан, а адрес карточки — нет: такие настройки сервер игнорирует (ПРАВИЛО 5.2), рабочая форма — адрес задан явно.'
      : null,
    RULES,
  ]
    .filter((p): p is string => p !== null)
    .join(' ');
}

const POLICY_NOTE =
  'Так решено в «Настройках»: «Все посты — расширенным сообщением». Версия уходит тем ' +
  'способом, который в неё подписан при сохранении: выключение политики вернёт карточку ' +
  'только версиям, сохранённым после него.';

/**
 * Карточка, которую получила бы НОВАЯ версия из тех же авторских частей при выключенной
 * политике. Вопрос задаёт одна ветка — версия подписана расширенной по политике, а политику с
 * тех пор выключили: «карточку вернёт новая версия» правда, только если в тексте есть ссылка,
 * которая её порождает. Форма — та, что выберет заморозка при сохранении правки
 * (`withDerivedTransport`: метод и опции карточки выводятся по тексту), правило — `getFirstUrl`.
 */
export function cardAfterResave(canonical: DraftContent): ReturnType<typeof getFirstUrl> {
  return getFirstUrl(
    withDerivedTransport(canonical, canonical.text, canonical.entities, { richAlways: false }),
  );
}

/** Версия подписана расширенной по политике, которую потом выключили, — фраза плашки. */
export const SAVED_UNDER_POLICY =
  'версия сохранена, когда все посты уходили расширенным сообщением';

export interface LinkPreviewStripProps {
  readonly canonical: DraftContent;
  /**
   * Показана НЕСОХРАНЁННАЯ правка: способ отправки не читается из `method`, а выводится по
   * политике бренда СЕЙЧАС — той, по которой правку заморозит сохранение.
   */
  readonly unsaved?: boolean;
  /** Политика бренда «все посты — расширенным сообщением» СЕЙЧАС; `undefined` — неизвестна. */
  readonly richAlways?: boolean | undefined;
  /** Действие в полосе карточки: «Перевести в расширенное» у версии, сохранённой обычным. */
  readonly action?: ReactNode;
}

export function LinkPreviewStrip({
  canonical,
  unsaved = false,
  richAlways,
  action = null,
}: LinkPreviewStripProps): JSX.Element {
  const outcome = unsaved
    ? getFirstUrl({
        ...canonical,
        method: undefined,
        richAlways: richAlways ?? TRANSPORT_RICH_ALWAYS_DEFAULT,
      })
    : getFirstUrl(canonical);
  const lpo = canonical.link_preview_options;
  const cp = codePointLen(canonical.text) as unknown as number;

  const strip = (row: JSX.Element): JSX.Element => (
    <div className="lps">
      {row}
      {action === null ? null : <div className={styles['lpsAction']}>{action}</div>}
    </div>
  );

  if (outcome.kind === 'card') {
    const side = lpo?.show_above_text === true ? 'Сверху' : 'Снизу';
    return strip(
      <div className="lps-t" title={cardTitle(outcome, lpo)}>
        <IconLink />
        <span>
          {side} прицепится карточка —{' '}
          <span className="mono" style={{ fontSize: 12 }}>
            {outcome.host}
          </span>
        </span>
      </div>,
    );
  }

  if (outcome.reason === 'rich_media') {
    return strip(
      <div className="lps-t" title={`Картинка встанет первым блоком, перед текстом. ${RICH_NOTE}`}>
        <IconInfo />
        <span>Карточки не будет: в посте картинка, пост уходит расширенным сообщением.</span>
      </div>,
    );
  }

  if (outcome.reason === 'rich_document') {
    return strip(
      <div className="lps-t" title={`Файл встанет последним блоком, после текста. ${RICH_NOTE}`}>
        <IconInfo />
        <span>Карточки не будет: к посту приложен файл, пост уходит расширенным сообщением.</span>
      </div>,
    );
  }

  if (outcome.reason === 'rich_length') {
    // С включённой политикой совет «уберите N» стал бы ложью: сокращённый пост всё равно уйдёт
    // расширенным. Длина остаётся причиной — ею пост уехал бы и без политики.
    return strip(
      <div
        className="lps-t"
        title={
          richAlways === true
            ? `Картинка тут ни при чём. Короче карточки тоже не было бы: ${POLICY_NOTE} ${RICH_NOTE}`
            : `Картинка тут ни при чём. ${RICH_NOTE}`
        }
      >
        <IconWarn />
        <span>
          {richAlways === true
            ? `Карточки не будет: ${String(cp)} кодпоинтов при пороге ${String(SEND_MESSAGE_THRESHOLD)} — пост уходит расширенным сообщением.`
            : `Карточки не будет: ${String(cp)} кодпоинтов при пороге ${String(SEND_MESSAGE_THRESHOLD)} — пост уходит расширенным сообщением, вернуть карточку значит убрать ${String(cp - SEND_MESSAGE_THRESHOLD)}.`}
        </span>
      </div>,
    );
  }

  if (outcome.reason === 'rich_policy' && richAlways === false) {
    // Политику выключили ПОСЛЕ сохранения версии: версия уходит так, как подписана, но «все
    // посты бренда уходят расширенным» уже неправда — «Настройки» говорят «Нет» (ревью,
    // ui-10). Видно откуда у версии этот способ и что его меняет; остальное — подсказкой.
    // Причина `rich_policy` у несохранённой правки при выключенной политике не возникает:
    // её способ выводится по политике СЕЙЧАС.
    const after = cardAfterResave(canonical);
    return strip(
      <div
        className="lps-t"
        title={
          'Политику «Все посты — расширенным сообщением» выключили после сохранения этой ' +
          'версии, а версия уходит тем способом, который в неё подписан. Новая версия с этим ' +
          'текстом уйдёт обычным сообщением' +
          (after.kind === 'card'
            ? ` — с карточкой: ${after.host}.`
            : ', но без карточки: в тексте нет ссылки, которая её порождает.') +
          ` ${RICH_NOTE}`
        }
      >
        <IconInfo />
        <span>
          {after.kind === 'card'
            ? `Карточки не будет: ${SAVED_UNDER_POLICY}. Карточку вернёт новая версия.`
            : `Карточки не будет: ${SAVED_UNDER_POLICY}.`}
        </span>
      </div>,
    );
  }

  if (outcome.reason === 'rich_policy') {
    return strip(
      <div className="lps-t" title={`${POLICY_NOTE} ${RICH_NOTE}`}>
        <IconInfo />
        <span>Карточки не будет: все посты бренда уходят расширенным сообщением (Настройки).</span>
      </div>,
    );
  }

  return strip(
    <div className="lps-t" title={RULES}>
      <IconInfo />
      <span>
        {outcome.reason === 'disabled'
          ? 'Карточки не будет: она отключена в этой версии черновика.'
          : 'Карточки не будет: в тексте нет ссылки, которая её порождает.'}
      </span>
    </div>,
  );
}
